'use client';

import { configKeepDataIn, configKeepDataInfiniy } from '@/config/useQuery';
import { randomuuid } from '@/lib/utils';
import { AdministrativeUnitResponse } from '@/server/api/v1/administrative-unit/type';
import {
    FilterBuilders,
    LogicalOperator,
    OperatorType,
} from '@/server/base/base-trpc-service';
import { api } from '@/trpc/client';
import { AdministrativeUnitVersion } from '@/types/enum';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import CustomSelect, { ComboboxOption, ComboboxProps } from '../custom-select';
import { AdministrativeUnitLevel } from './constants';

type SelectAdministrativeUnitProps = ComboboxProps & {
    level: AdministrativeUnitLevel;

    /**Code của level cha nếu có */
    parentCode?: string;

    /**ParentId */
    parentId?: string;

    /**Value của level hiện tại */
    value?: string;

    /**
     * Tên đơn vị hành chính
     * Trường này sẽ được sử dụng khi nó có data và value không có
     */
    labelName?: string;

    onSelect?: (value?: AdministrativeUnitResponse) => void;

    /**Chặn gọi API */
    disabledApi: boolean;

    /**
     * Detect container for select popover
     */
    componentID?: string;
};

function createFilter() {
    const filters: FilterBuilders<AdministrativeUnitResponse>[] = [
        {
            key: 'Version',
            values: [AdministrativeUnitVersion.Level2],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.Equal,
        },
    ];
    return filters;
}

/**
 * Component chọn đơn vị hành chính
 */
export const SelectUnit = memo((props: SelectAdministrativeUnitProps) => {
    const {
        level,
        onSelect,
        value,
        labelName,
        parentCode,
        parentId,
        disabledApi,
        componentID,
        ...rest
    } = props;

    const transDef = useTranslations('System');

    const [options, setOptions] = useState<
        ComboboxOption<AdministrativeUnitResponse>[]
    >([]);

    /**
     * Điều kiện để gọi API query data
     */
    const isEnableQueryTree =
        level === AdministrativeUnitLevel.province ||
        (!!parentId &&
            (level === AdministrativeUnitLevel.district ||
                level === AdministrativeUnitLevel.commnue));

    /**
     * Lấy dữ liệu từ API
     */
    const { data: apiUnits, isLoading: loading_units } =
        api.administrativeUnit.GetTree.useQuery(
            {
                numOfParentBefore: 0,
                treeDepth: level === AdministrativeUnitLevel.commnue ? 1 : 0,
                filters: createFilter(),
                id: parentId,
            },
            {
                ...configKeepDataIn(60),
                enabled: !!isEnableQueryTree && !disabledApi,
            }
        );

    /**
     * Xử lý nếu có scroll to load
     * TODO: Chưa làm
     */
    const handleOnScrollToEndList = useCallback(
        (target: EventTarget & HTMLDivElement) => {
            if (
                target.scrollHeight - target.scrollTop <=
                target.clientHeight + 50
            ) {
            }
        },
        []
    );

    useEffect(() => {
        if (!isEnableQueryTree) {
            setOptions([]);
        } else if (apiUnits?.isSuccessStatus && apiUnits.data?.Result) {
            setOptions((st) => {
                const otps =
                    apiUnits.data?.Result.map((item) => ({
                        label: item.Name,
                        value: item.Origin_Def_Code,
                        _origin: item,
                    })) ?? [];

                return otps.sort((a, b) => a.label.localeCompare(b.label));
            });
        }
    }, [apiUnits?.data?.Result, apiUnits?.isSuccessStatus, isEnableQueryTree]);

    const _isCheckedLabelName = useRef<boolean>(false);

    /**
     * Khi có labelName thì sẽ tìm kiếm trong options
     *
     * TODO cái này có nguy cơ loop
     */
    useEffect(() => {
        // if (_isCheckedLabelName.current) return;
        if (options.length && labelName && !value) {
            const found = options.find(
                (opt) => opt.label === labelName || opt.value === labelName
            );
            if (found) {
                onSelect?.(found._origin);
                _isCheckedLabelName.current = true;
            }
        }
        return () => {
            _isCheckedLabelName.current = false;
        };
    }, [labelName, onSelect, options, value]);

    const [selectUnitID] = useState(() => `Select_Unit_${randomuuid()}`);

    return (
        <div id={selectUnitID} className='w-full flex-1 font-medium'>
            <CustomSelect.Single
                {...rest}
                // disabled={!isEnableQueryTree}
                isSearching={loading_units}
                // showCommand={false}
                itemForceMount={false}
                value={value}
                onAfterValueChange={(_, opt) => {
                    onSelect?.(opt?._origin);
                }}
                options={options}
                onScrollCommandList={(e) => {
                    handleOnScrollToEndList(e.currentTarget);
                }}
                placeholder={`${transDef('System_Select')} ${(level ===
                AdministrativeUnitLevel.province
                    ? transDef('System_Province')
                    : level === AdministrativeUnitLevel.commnue
                      ? transDef('System_Ward')
                      : ''
                ).toLowerCase()}. . .`}
                // container={document.body}
            />
        </div>
    );
});

SelectUnit.displayName = 'SelectUnit';
